import { useLanguage } from '../i18n/content';
import { FadeUp, Eyebrow } from './Reveal';

/*
 * Modular service card. Driven entirely by data:
 * add a future NOFINA business unit to t.services.items (with an optional
 * `href` / `external` for a subdomain or page) and it renders here with no
 * layout redesign.
 */
function ServiceCard({ item }) {
  const inner = (
    <>
      <div className="relative h-44 overflow-hidden sm:h-48">
        <img
          src={item.image}
          alt={item.alt}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
        />
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-t from-nofina-card via-transparent to-transparent"
        />
      </div>
      <div className="relative p-5 pt-3 sm:p-6 sm:pt-3">
        <h3 className="font-display text-2xl font-bold tracking-wide text-white">
          {item.name} <span className="text-nofina-red">{item.accent}</span>
        </h3>
        <p className="mt-1 text-sm font-semibold text-slate-200">
          {item.subtitle}
        </p>
        <p className="mt-3 text-sm leading-relaxed text-slate-400">
          {item.desc}
        </p>
      </div>
    </>
  );

  const cls =
    'group relative h-full overflow-hidden rounded-2xl border border-white/10 bg-nofina-card text-left transition-all duration-500 hover:border-nofina-red/50 hover:shadow-[0_24px_60px_-24px_rgba(229,9,20,0.4)]';

  if (item.href) {
    return (
      <a
        href={item.href}
        data-testid={`layanan-card-nofina-${item.id}`}
        className={cls}
        {...(item.external
          ? { target: '_blank', rel: 'noreferrer noopener' }
          : {})}
      >
        {inner}
      </a>
    );
  }
  return (
    <article data-testid={`layanan-card-nofina-${item.id}`} className={cls}>
      {inner}
    </article>
  );
}

export function Services() {
  const { t } = useLanguage();

  return (
    <section
      id="layanan"
      className="relative overflow-hidden bg-[#0D0D11] py-16 lg:py-20"
    >
      <div
        aria-hidden
        className="halftone-silver pointer-events-none absolute right-0 top-0 hidden h-52 w-36 opacity-50 [mask-image:linear-gradient(to_bottom,black,transparent)] lg:block"
      />
      <div className="relative mx-auto grid max-w-7xl gap-10 px-4 sm:px-8 md:grid-cols-12 md:gap-8 lg:px-10">
        <FadeUp className="md:col-span-4">
          <Eyebrow>{t.services.eyebrow}</Eyebrow>
          <h2
            className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl"
            data-testid="services-title"
          >
            {t.services.titleA}
            <span className="block">
              {t.services.titleB}
              <span className="text-nofina-red">.</span>
            </span>
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-400">
            {t.services.intro}
          </p>
        </FadeUp>

        <div className="md:col-span-8">
          <div className="grid h-full gap-5 sm:grid-cols-2">
            {t.services.items.map((item, i) => (
              <FadeUp key={item.id} delay={0.1 + i * 0.12} className="h-full">
                <ServiceCard item={item} />
              </FadeUp>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
